import React from 'react';
import { AppBar, Toolbar, CssBaseline, styled, IconButton, Box, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { LAYOUT_CONFIG, LayoutMode } from '../../../../constants/layout';
import { useLanguage } from '../../../../context/LanguageContext';
import { useBrand } from '../../../../context/BrandContext';
import LanguageSelector from './LanguageSelector';
import LoyaltyCardButton from './LoyaltyCardButton';
import UserMenu from './UserMenu';
import HorizontalMenuList from '../Menu/HorizontalMenuList';
import { useMenuStructure } from '../../hooks/useMenuStructure';
import { useNavigate, useLocation } from 'react-router-dom';
import { MenuItem } from '../../types';

const AppBarStyled = styled(AppBar, {
    shouldForwardProp: (prop) => prop !== 'open' && prop !== 'layoutMode',
})<{ open?: boolean; layoutMode?: LayoutMode }>(({ theme }) => {
    return {
        height: LAYOUT_CONFIG.headerHeight,
        backgroundColor: '#ffffff',
        boxShadow: '0 0.75rem 1.5rem rgba(18, 38, 63, 0.03)',
        left: 0,
        width: '100%',
        transition: LAYOUT_CONFIG.transition,
        [theme.breakpoints.down('md')]: {
            left: 0,
            width: '100%',
        },
    };
});

const LogoLink = styled(Link)({
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    cursor: 'pointer',
});

interface HeaderProps {
    open: boolean;
    onToggle: () => void;
    layoutMode?: LayoutMode;
    onLayoutModeChange?: (mode: LayoutMode) => void;
}

const toggleFullscreen = () => {
    const doc = document as any;
    const docEl = document.documentElement as any;
    
    const isFullscreen = document.fullscreenElement || doc.mozFullScreenElement || doc.webkitFullscreenElement;
    
    if (!isFullscreen) {
        const requestMethod = docEl.requestFullscreen || docEl.mozRequestFullScreen || docEl.webkitRequestFullscreen;
        requestMethod?.call(docEl);
    } else {
        const exitMethod = doc.exitFullscreen || doc.mozCancelFullScreen || doc.webkitExitFullscreen;
        exitMethod?.call(doc);
    }
};

const LayoutModeButton: React.FC<{
    mode: LayoutMode;
    currentMode: LayoutMode;
    icon: string;
    title: string;
    onClick: (mode: LayoutMode) => void;
}> = ({ mode, currentMode, icon, title, onClick }) => {
    const isActive = currentMode === mode;
    return (
        <Tooltip title={title}>
            <IconButton 
                onClick={() => onClick(mode)}
                sx={{
                    color: isActive ? '#556ee6' : '#495057',
                    backgroundColor: isActive ? 'rgba(85, 110, 230, 0.1)' : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: isActive ? 'rgba(85, 110, 230, 0.15)' : 'rgba(73, 80, 87, 0.1)',
                        transform: 'translateY(-2px)'
                    }
                }}
            >
                <i className={icon} style={{ fontSize: 20 }} />
            </IconButton>
        </Tooltip>
    );
};

const Header: React.FC<HeaderProps> = ({ open, onToggle, layoutMode = 'vertical', onLayoutModeChange }) => {
    const { t } = useLanguage();
    const { brand } = useBrand();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const location = useLocation();
    const menuStructure = useMenuStructure();

    const handleMenuItemClick = React.useCallback((item: MenuItem) => {
        if (item.path) navigate(item.path);
    }, [navigate]);

    return (
        <>
            <CssBaseline />
            <AppBarStyled position="fixed" open={open} layoutMode={layoutMode}>
                <Toolbar sx={{ justifyContent: 'space-between', minHeight: `${LAYOUT_CONFIG.headerHeight}px !important`, px: 3 }}>
                    <LogoLink to="/dashboard">
                        <img
                            src={brand.logo}
                            alt={brand.displayName}
                            style={{ 
                                height: isMobile ? 30 : 40, 
                                width: 'auto', 
                                maxWidth: isMobile ? 100 : 150, 
                                objectFit: 'contain' 
                            }}
                        />
                    </LogoLink>

                    {!isMobile && (
                        <Box sx={{ 
                            flex: 1, 
                            display: 'flex', 
                            justifyContent: 'center',
                            mx: 4
                        }}>
                            <HorizontalMenuList
                                menuItems={menuStructure}
                                onItemClick={handleMenuItemClick}
                                currentPath={location.pathname}
                            />
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 1 }, alignItems: 'center' }}>
                        <LoyaltyCardButton />
                        <LanguageSelector />
                        
                        {!isMobile && (
                            <Tooltip title={t('header.fullscreen')}>
                                <IconButton onClick={toggleFullscreen} sx={{ color: '#495057' }}>
                                    <i className="bx bx-fullscreen" style={{ fontSize: 22 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        
                        <UserMenu />
                    </Box>
                </Toolbar>
            </AppBarStyled>
        </>
    );
};

export default Header;
